import React from "react";
import {
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  ScrollView,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { TOPICS } from "../../../constants/topics";
import DateTimePicker from "../../CreateRoom/ui/DateTimePicker";
import TopicCard from "../../CreateRoom/ui/TopicCars";
import type { EditRoomViewProps } from "../editRoom.types";
import { styles } from "./EditRoomView.shared";
import Dropdown from "../../../common/Dropdown";
import { PREDEFINED_MEETING_NAMES } from "../../CreateRoom/ui/CreateRoomView.shared";

export default function EditRoomViewMobile({
  roomName,
  roomNameError,
  selectedDate,
  selectedHour,
  selectedMinute,
  selectedAmPm,
  selectedTopicIds,
  customTopicText,
  isSubmitting,
  canSubmit,
  currentTimezone,
  onChangeRoomName,
  onChangeDate,
  onChangeHour,
  onChangeMinute,
  onChangeAmPm,
  onToggleTopic,
  onChangeCustomTopic,
  onSubmit,
  onCancel,
  selectedMeetingNameOption,
  onChangeSelectedMeetingNameOption,
  maxParticipants,
  onChangeMaxParticipants,
}: EditRoomViewProps) {
  return (
    <SafeAreaView style={styles.root}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <View style={[styles.content, styles.contentMobile]}>
            <View style={styles.mainContent}>
              <Text style={[styles.pageTitle, styles.pageTitleMobile]}>
                Edit Conversation Room
              </Text>
              <Text style={[styles.subtitle, styles.subtitleMobile]}>
                Update the details for this conversation.
              </Text>

              <View style={[styles.formCard, styles.formCardMobile]}>
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>CONVERSATION NAME</Text>
                  <Dropdown
                    label="Select Predefined Name or Make Your Own"
                    value={selectedMeetingNameOption}
                    options={PREDEFINED_MEETING_NAMES}
                    onChange={onChangeSelectedMeetingNameOption}
                  />
                </View>

                {selectedMeetingNameOption === "Custom / Make your own..." && (
                  <View style={styles.section}>
                    <Text style={styles.sectionTitle}>
                      CUSTOM CONVERSATION NAME
                    </Text>
                    <TextInput
                      value={roomName}
                      onChangeText={onChangeRoomName}
                      placeholder="Let's talk about hobbies, dreams, healthy lifestyle"
                      placeholderTextColor="#b1afaf"
                      style={[
                        styles.input,
                        styles.inputMobile,
                        roomNameError && styles.inputError,
                      ]}
                    />
                    {roomNameError && (
                      <Text style={styles.errorText}>{roomNameError}</Text>
                    )}
                  </View>
                )}

                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>MAX PARTICIPANTS</Text>
                  <Dropdown
                    label="Select capacity (up to 4)"
                    value={`${maxParticipants} participants`}
                    options={[
                      "2 participants",
                      "3 participants",
                      "4 participants",
                    ]}
                    onChange={(val) => {
                      const count = parseInt(val.split(" ")[0], 10);
                      onChangeMaxParticipants(count);
                    }}
                  />
                </View>

                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>PICK YOUR TIME</Text>
                  <DateTimePicker
                    selectedDate={selectedDate}
                    selectedHour={selectedHour}
                    selectedMinute={selectedMinute}
                    selectedAmPm={selectedAmPm}
                    onDateChange={onChangeDate}
                    onHourChange={onChangeHour}
                    onMinuteChange={onChangeMinute}
                    onAmPmChange={onChangeAmPm}
                    variant="mobile"
                  />
                  <Text style={[styles.helperText, styles.helperTextMobile]}>
                    Time will be displayed in each participant&#39;s local
                    timezone. Your timezone: {currentTimezone}
                  </Text>
                </View>
              </View>

              <View style={styles.topicsSection}>
                <Text style={styles.sectionTitle}>TOPICS</Text>
                <View
                  style={[
                    styles.topicsGridContainer,
                    styles.topicsGridContainerMobile,
                  ]}
                >
                  {TOPICS.map((topic) => (
                    <TopicCard
                      key={topic.id}
                      title={topic.label}
                      description={topic.description}
                      selected={selectedTopicIds.includes(topic.id)}
                      onToggle={() => onToggleTopic(topic.id)}
                      variant="mobile"
                    />
                  ))}
                </View>
              </View>
            </View>

            <View style={[styles.footerSection, styles.footerSectionMobile]}>
              <View style={[styles.footer, styles.footerMobile]}>
                <TouchableOpacity
                  onPress={onCancel}
                  style={[styles.cancelButton, styles.cancelButtonMobile]}
                >
                  <Text style={styles.cancelButtonText}>CANCEL</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  onPress={onSubmit}
                  style={[
                    styles.continueButton,
                    styles.continueButtonMobile,
                    (!canSubmit || isSubmitting) &&
                      styles.continueButtonDisabled,
                  ]}
                  disabled={!canSubmit || isSubmitting}
                >
                  <Text style={styles.continueButtonText}>
                    {isSubmitting ? "SAVING..." : "SAVE"}
                  </Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}
